import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TimetableSession } from "../../lib/api";
import { CategoryChips } from "./CategoryChips";
import { CalendarView } from "./CalendarView";
import { openCheckoutOverlay } from "./checkout";
import { fetchMonthSessions } from "./data";
import {
  addMonths,
  compareYearMonth,
  dayKeyInTz,
  groupSessionsByDay,
  isInMonth,
  monthKey,
  monthTitle,
  resolveCategory,
  type YearMonth,
} from "./lib";
import { ListView } from "./ListView";
import { WHATS_ON_CSS } from "./styles";
import { CATEGORY_MAP, FALLBACK_CATEGORY, FONTS, type CategoryStyle } from "./theme";

const CATEGORY_ORDER = [...Object.keys(CATEGORY_MAP), FALLBACK_CATEGORY.key];

export type WhatsOnProps = {
  school: string;
  apiUrl?: string;
  primaryColor?: string;
  gtagConversionId?: string;
  gtagConversionLabel?: string;
};

function currentYearMonth(): YearMonth {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

export function WhatsOn({ school, apiUrl, primaryColor, gtagConversionId, gtagConversionLabel }: WhatsOnProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const minMonth = useMemo(() => currentYearMonth(), []);
  const [ym, setYm] = useState<YearMonth>(minMonth);
  const [view, setView] = useState<"calendar" | "list">("calendar");
  const [disabled, setDisabled] = useState<ReadonlySet<string>>(new Set());
  const [highlightSessionId, setHighlightSessionId] = useState<string>();
  const [scrollDayKey, setScrollDayKey] = useState<string>();

  // Newsreader + Work Sans into the shadow root (theme.ts:330-336 pattern)
  useEffect(() => {
    const shadow = rootRef.current?.getRootNode();
    if (!(shadow instanceof ShadowRoot)) return;
    if (shadow.querySelector("link[data-bwo-fonts]")) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONTS.googleCssUrl;
    link.setAttribute("data-bwo-fonts", "");
    shadow.appendChild(link);
  }, []);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["whats-on", school, monthKey(ym)],
    queryFn: () => fetchMonthSessions({ school, ym, apiUrl }),
    enabled: Boolean(school),
    // Freshness after a booking is handled explicitly by the
    // baluu:booking-completed invalidation listener below.
    staleTime: 5 * 60_000,
  });

  // refresh availability after any completed booking
  useEffect(() => {
    const onCompleted = () => queryClient.invalidateQueries({ queryKey: ["whats-on", school] });
    document.addEventListener("baluu:booking-completed", onCompleted);
    return () => document.removeEventListener("baluu:booking-completed", onCompleted);
  }, [queryClient, school]);

  const tz = data?.sessions?.[0]?.course?.timezone || "Europe/London";

  const monthSessions = useMemo(
    () => (data?.sessions ?? []).filter((s) => isInMonth(dayKeyInTz(s.startDateTime, tz), ym)),
    [data, tz, ym],
  );

  const categories = useMemo<CategoryStyle[]>(() => {
    const found = new Map<string, CategoryStyle>();
    for (const s of monthSessions) {
      const cat = resolveCategory(s.course);
      if (!found.has(cat.key)) found.set(cat.key, cat);
    }
    return [...found.values()].sort(
      (a, b) => CATEGORY_ORDER.indexOf(a.key) - CATEGORY_ORDER.indexOf(b.key),
    );
  }, [monthSessions]);

  const active = useMemo(
    () => new Set(categories.map((c) => c.key).filter((k) => !disabled.has(k))),
    [categories, disabled],
  );

  const visibleSessions = useMemo(
    () => monthSessions.filter((s) => active.has(resolveCategory(s.course).key)),
    [monthSessions, active],
  );

  const days = useMemo(() => groupSessionsByDay(visibleSessions, tz), [visibleSessions, tz]);

  const locationsById = useMemo(
    () => new Map((data?.locations ?? []).map((l) => [l._id, l.name])),
    [data],
  );

  const handleBook = useCallback(
    (session: TimetableSession) =>
      openCheckoutOverlay(session, { apiUrl, primaryColor, gtagConversionId, gtagConversionLabel }),
    [apiUrl, primaryColor, gtagConversionId, gtagConversionLabel],
  );

  const jumpToSession = useCallback((s: TimetableSession) => {
    setView("list");
    setScrollDayKey(undefined);
    setHighlightSessionId(s._id);
  }, []);

  const jumpToDay = useCallback((dayKey: string) => {
    setView("list");
    setHighlightSessionId(undefined);
    setScrollDayKey(dayKey);
  }, []);

  const changeMonth = useCallback((delta: number) => {
    setHighlightSessionId(undefined);
    setScrollDayKey(undefined);
    setYm((cur) => addMonths(cur, delta));
  }, []);

  // A manual Calendar/List toggle should behave like a fresh view, not carry
  // over a previous jump's highlight/scroll target (which would re-scroll on
  // re-entering List).
  const changeView = useCallback((v: "calendar" | "list") => {
    setHighlightSessionId(undefined);
    setScrollDayKey(undefined);
    setView(v);
  }, []);

  return (
    <div className="bwo-root" ref={rootRef}>
      <style>{WHATS_ON_CSS}</style>
      <h2 className="bwo-title">What&rsquo;s On</h2>
      <CategoryChips
        categories={categories}
        active={active}
        onToggle={(key) =>
          setDisabled((cur) => {
            const next = new Set(cur);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          })
        }
      />
      <div className="bwo-controls">
        <div className="bwo-toggle" role="tablist">
          <button type="button" aria-pressed={view === "calendar"} onClick={() => changeView("calendar")}>
            Calendar
          </button>
          <button type="button" aria-pressed={view === "list"} onClick={() => changeView("list")}>
            List
          </button>
        </div>
        <div className="bwo-monthnav">
          <button
            type="button"
            aria-label="Previous month"
            disabled={compareYearMonth(ym, minMonth) <= 0}
            onClick={() => changeMonth(-1)}
          >
            ‹
          </button>
          <h3>{monthTitle(ym)}</h3>
          <button type="button" aria-label="Next month" onClick={() => changeMonth(1)}>
            ›
          </button>
        </div>
      </div>
      {isLoading ? (
        <div className="bwo-list">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bwo-skeleton" />
          ))}
        </div>
      ) : isError ? (
        <div className="bwo-error">
          Something went wrong loading events.
          <br />
          <button type="button" className="bwo-retry" onClick={() => refetch()}>
            Try again
          </button>
        </div>
      ) : days.size === 0 ? (
        <div className="bwo-empty">
          {monthSessions.length > 0
            ? "No events match your selected categories."
            : "Nothing scheduled this month yet — check back soon."}
        </div>
      ) : view === "calendar" ? (
        <CalendarView
          ym={ym}
          days={days}
          tz={tz}
          locationsById={locationsById}
          onSessionClick={jumpToSession}
          onMoreClick={jumpToDay}
          onBook={handleBook}
        />
      ) : (
        <ListView
          days={days}
          tz={tz}
          locationsById={locationsById}
          highlightSessionId={highlightSessionId}
          scrollDayKey={scrollDayKey}
          onBook={handleBook}
        />
      )}
    </div>
  );
}
